import {
  DownloadOutlined,
  FileOutlined,
  FolderAddOutlined
} from "@ant-design/icons"
import { Button, Image, Space, Tooltip, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { showNotification } from "../../../utils/notification/show-notification"
import { saveBlob } from "../../../utils/download-file.ts"
import { fetchAttachmentBlob } from "../api/services.ts"
import { useAttachmentBytes } from "../hooks/use-attachment-bytes.ts"
import type { MessageAttachment } from "../types/messaging.types.ts"

const formatSize = (bytes: number | null): string => {
  if (bytes === null) return ""
  const units = ["B", "KB", "MB", "GB"]
  let size = bytes
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`
}

interface AttachmentCardProps {
  attachment: MessageAttachment
  canSaveToProjectFiles: boolean
  portal?: boolean
  onSaveToProjectFiles?: (attachment: MessageAttachment) => void
}

export function AttachmentCard({
  attachment,
  canSaveToProjectFiles,
  portal = false,
  onSaveToProjectFiles
}: AttachmentCardProps) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)

  // The bytes are served only through the authorized route, and that route
  // wants the caller's token. A browser-issued request for an <img src> or an
  // <a href> carries no Authorization header and resolves against the page
  // origin rather than the API's, so it fetched the SPA's own HTML and the
  // preview rendered broken. Both now go through the API client.
  const { url: source, failed } = useAttachmentBytes(attachment.id, {
    enabled: attachment.is_image,
    portal
  })

  const handleDownload = async () => {
    setSaving(true)
    const response = await fetchAttachmentBlob(attachment.id, portal)
    setSaving(false)
    if (!response.ok || !response.data) {
      showNotification.error({
        message: t(
          "messages.attachment.downloadFailed",
          "That file could not be downloaded."
        ) as string
      })
      return
    }
    saveBlob(response.data, attachment.file_name)
  }

  return (
    <div
      className={
        "mt-2 rounded-lg border border-slate-200 bg-white p-2 max-w-sm"
      }
    >
      {attachment.is_image && source && (
        <Image
          src={source}
          alt={attachment.file_name}
          className={"rounded-md"}
          style={{ maxHeight: 220, objectFit: "cover" }}
        />
      )}
      {attachment.is_image && !source && (
        <div
          className={
            "flex h-24 items-center justify-center rounded-md bg-slate-50 text-xs text-slate-500"
          }
        >
          {failed
            ? t("messages.attachment.previewFailed", "Preview unavailable")
            : t("messages.attachment.previewLoading", "Loading preview…")}
        </div>
      )}

      <Space align={"center"} className={"w-full mt-1"} size={"small"}>
        <FileOutlined className={"text-slate-500"} />
        <div className={"min-w-0 flex-1"}>
          <Typography.Text ellipsis className={"block text-sm"}>
            {attachment.file_name}
          </Typography.Text>
          <Typography.Text type={"secondary"} className={"text-xs"}>
            {[attachment.content_type, formatSize(attachment.file_size)]
              .filter(Boolean)
              .join(" · ")}
          </Typography.Text>
        </div>

        <Tooltip title={t("messages.attachment.download", "Download")}>
          <Button
            size={"small"}
            type={"text"}
            icon={<DownloadOutlined />}
            loading={saving}
            onClick={handleDownload}
            aria-label={t("messages.attachment.download", "Download")}
          />
        </Tooltip>

        {canSaveToProjectFiles && (
          <Tooltip
            title={
              attachment.saved_to_project_files
                ? t(
                    "messages.attachment.alreadySaved",
                    "Already in Project Files"
                  )
                : t("messages.attachment.saveToFiles", "Save to Project Files")
            }
          >
            <Button
              size={"small"}
              type={"text"}
              disabled={attachment.saved_to_project_files}
              icon={<FolderAddOutlined />}
              onClick={() => onSaveToProjectFiles?.(attachment)}
              aria-label={t(
                "messages.attachment.saveToFiles",
                "Save to Project Files"
              )}
            />
          </Tooltip>
        )}
      </Space>
    </div>
  )
}
